import type {
  WalletConnectConfig,
  WalletMeta,
  ConnectResult,
  InitSessionResponse,
  SendTransactionParams,
  SendTransactionResult,
  WcPayParams,
  WaitForResultOptions,
  TxStatusResult,
  WalletConnectEventType,
  WalletConnectEventCallback,
} from './types';
import { WsTransport, PollingTransport } from './transport';
import { Modal } from './modal';
import { openWalletWindow } from './open-wallet-window';

const STORAGE_KEY_PREFIX = '__wc_session_';
const PENDING_TTL_MS = 5 * 60 * 1000; // bỏ qua pending cũ hơn 5 phút (= session TTL)
// After "Open in Telegram" on web, the wallet opens in the Telegram app and the launcher window we
// opened is a stray — close it shortly after so it doesn't linger behind the dApp.
const WALLET_WINDOW_CLOSE_MS = 3000;
// Default backend: the SDK reads the live wallet list from ${DEFAULT_API_URL}/connect/wallets (DB).
// A dApp only sets `apiUrl` to point at another environment (staging/dev).
const DEFAULT_API_URL = 'https://onewallet-api-staging.motcaigido.xyz';

/** Per-wallet accent color fallback used when the registry entry omits `accent`. */
const WALLET_ACCENTS: Record<string, string> = {
  tonpass: '#0a0a0a',
};

/** Wallets rendered as a flat white sheet with a black/white palette. */
const MONO_WALLETS = new Set<string>(['tonpass']);

export class BaseWalletConnect {
  protected config: Required<
    Pick<WalletConnectConfig, 'appId' | 'registryUrl' | 'theme'>
  > &
    Pick<
      WalletConnectConfig,
      'walletId' | 'siteTmaUrl' | 'network' | 'appName' | 'accent' | 'returnAT'
    >;

  private listeners: Map<WalletConnectEventType, Set<WalletConnectEventCallback>> = new Map();
  private modal: Modal | null = null;
  private wsTransport: WsTransport | null = null;
  private pollingTransport: PollingTransport | null = null;
  private currentResult: ConnectResult | null = null;
  private initPromise: Promise<void> = Promise.resolve();
  // How the current connect was made: 'deeplink' = opened the wallet on this device (desktop window),
  // 'qr' = scanned with a phone. Decides whether pay() opens the desktop wallet window.
  private connectVia: 'qr' | 'deeplink' = 'qr';
  private walletWindow: Window | null = null;

  constructor(config: WalletConnectConfig) {
    this.config = {
      appId: config.appId,
      // Read the live wallet list from the backend's /connect/wallets (DB). Defaults to the prod
      // backend; the dApp overrides via `apiUrl` (env) or `registryUrl` (full URL).
      registryUrl:
        config.registryUrl ||
        `${(config.apiUrl || DEFAULT_API_URL).replace(/\/$/, '')}/connect/wallets`,
      walletId: config.walletId,
      theme: config.theme || 'dark',
      siteTmaUrl: config.siteTmaUrl,
      network: config.network,
      appName: config.appName,
      accent: config.accent,
      returnAT: config.returnAT ?? false,
    };

    this.restoreSession();
    this.registerGlobal();
    // Init tuần tự: resume connect (nếu vừa approve xong) rồi kiểm tra session hết hạn.
    // DApp có thể `await wc.ready()` để chắc chắn init xong TRƯỚC khi đọc isConnected()/getWallet().
    this.initPromise = this.runInit();
  }

  /**
   * Init flow chạy lúc khởi tạo: (1) resumeConnect — CHỈ cho connect pending (tự bật modal +
   * emit 'connect'); (2) validateSession — kiểm tra session đã lưu còn sống trên server không,
   * hết hạn thì xoá localStorage + toast. Tx resume do DApp tự gọi resume().
   */
  private async runInit(): Promise<void> {
    await this.resumeConnect();
    await this.validateSession();
  }

  /** Chờ init xong (resume + kiểm tra hết hạn) trước khi đọc trạng thái kết nối. */
  async ready(): Promise<void> {
    await this.initPromise;
  }

  // ── Public API ──

  async connect(): Promise<ConnectResult> {
    if (this.currentResult) {
      return this.currentResult;
    }

    const wallets = await this.fetchWallets();
    const filtered = this.filterWallets(wallets);

    if (filtered.length === 0) {
      throw new Error('No supported wallets available');
    }

    return new Promise<ConnectResult>((resolve, reject) => {
      const onClose = () => {
        this.cleanup();
        reject(new Error('User closed modal'));
      };

      this.modal = new Modal(this.config.theme, onClose);

      if (filtered.length === 1) {
        this.startConnect(filtered[0], resolve, reject);
      } else {
        this.modal.showWalletPicker(filtered, (wallet) => {
          this.startConnect(wallet, resolve, reject);
        });
      }
    });
  }

  // The user clicked "Open in Telegram" in the connect modal → the wallet opens on THIS device.
  // Inside Telegram deep-link it; on web open the phone-sized desktop window and keep the reference.
  private openWalletForConnect(deeplink: string): void {
    this.connectVia = 'deeplink';
    const tg = typeof window !== 'undefined' ? (window as any).Telegram?.WebApp : null;
    if (tg?.openTelegramLink) {
      tg.openTelegramLink(deeplink);
      return;
    }
    this.openWallet(deeplink);
  }

  // Open the wallet launcher window, then close it after WALLET_WINDOW_CLOSE_MS so the stray launcher
  // doesn't linger behind the dApp once the Telegram app has taken over.
  private openWallet(url: string): void {
    this.walletWindow = openWalletWindow(url);
    const w = this.walletWindow;
    if (!w) return;
    setTimeout(() => {
      try {
        if (!w.closed) w.close();
      } catch {
        // window already gone / cross-origin — nothing to do
      }
    }, WALLET_WINDOW_CLOSE_MS);
  }

  // On web (not inside a TMA) append the markers the wallet reads from the connect startapp, in order:
  //   _nc          → keep the Mini App open after approve/reject
  //   _<appName>   → the dApp's display name. Telegram's startapp only allows [A-Za-z0-9_-], so any
  //                  space/other invalid char is stripped (a raw space becomes %20 and Telegram
  //                  refuses to open the Mini App) — e.g. "ONE Plans" → "ONEPlans". Same decorated
  //                  link feeds the QR and the "Open in Telegram" button; inside Telegram it's left as-is.
  private withConnectMarkers(deeplink: string): string {
    if (typeof window === 'undefined') return deeplink;
    if ((window as any).Telegram?.WebApp?.openTelegramLink) return deeplink;
    if (!deeplink.includes('startapp=')) return deeplink;
    let out = deeplink;
    if (!out.includes('_nc')) out += '_nc';
    const name = this.config.appName?.replace(/[^A-Za-z0-9_-]/g, '');
    if (name && !out.endsWith(`_nc_${name}`)) out += `_${name}`;
    return out;
  }

  // Wallet Mini App launch link, rebuilt from the session so we can reopen it to sign a tx.
  private walletLaunchUrl(): string | null {
    const tma = this.currentResult?.session?.tma;
    if (!tma?.botUsername || !tma?.appName) return null;
    return `https://t.me/${tma.botUsername}/${tma.appName}`;
  }

  // Bring up the wallet to sign. Inside Telegram deep-link it. On web: only when connected on THIS
  // device ('deeplink') — focus the open wallet window, or reopen it if the user closed it. For a QR
  // connect the wallet lives on the phone and receives the tx over the socket, so open nothing here.
  private openWalletForTx(): void {
    const tg = typeof window !== 'undefined' ? (window as any).Telegram?.WebApp : null;
    const launchUrl = this.walletLaunchUrl();
    if (tg?.openTelegramLink) {
      if (launchUrl) tg.openTelegramLink(launchUrl);
      return;
    }
    if (this.currentResult?.session?.via !== 'deeplink') return; // QR → phone gets it via socket
    if (this.walletWindow && !this.walletWindow.closed) {
      this.walletWindow.focus();
      return;
    }
    if (launchUrl) this.openWallet(launchUrl);
  }

  disconnect(): void {
    // Find session from this instance or any other connected instance
    let session = this.currentResult?.session;
    if (!session && typeof window !== 'undefined') {
      for (const key of Object.keys(window)) {
        const inst = (window as any)[key];
        if (inst instanceof BaseWalletConnect && inst.currentResult?.session) {
          session = inst.currentResult.session;
          break;
        }
      }
    }

    // Call disconnect API (fire-and-forget)
    if (session) {
      const baseUrl = session.apiUrl.replace(/\/$/, '');
      fetch(`${baseUrl}/connect/disconnect-by-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionToken: session.sessionToken }),
      }).catch(() => {});
    }

    // Clear all localStorage keys and window instances
    this.currentResult = null;
    this.removeStoredSession();
    this.clearAllRelatedSessions();
    this.cleanup();
    this.emit('disconnect');
  }

  async sendTransaction(
    params: SendTransactionParams,
  ): Promise<SendTransactionResult> {
    // Gate: xác thực phiên còn sống trên server trước khi gửi. Hết hạn -> validateSession
    // tự clear + emit 'disconnect', currentResult về null -> ném 'Wallet not connected'.
    await this.validateSession();

    const session = this.currentResult?.session;
    if (!session?.apiUrl) {
      throw new Error('Wallet not connected');
    }

    const baseUrl = session.apiUrl.replace(/\/$/, '');

    let res: Response;
    try {
      res = await fetch(`${baseUrl}/dex/sendTx`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...params,
          appId: this.config.appId,
          source: 'widget',
        }),
      });
    } catch {
      throw new Error('Connection failed');
    }

    if (res.status === 401) {
      throw new Error('Wallet not connected');
    }
    if (res.status === 429) {
      throw new Error('Too many pending transactions');
    }
    if (!res.ok) {
      throw new Error('Failed to send transaction');
    }

    const data = await res.json();

    // Lưu pending để nếu onedex đóng (mở onewallet ký) rồi mở lại -> tự check /dex/status.
    this.storePending({
      type: 'tx',
      txId: data.txId,
      apiUrl: session.apiUrl,
      statusPath: '/dex/status',
      ts: Date.now(),
    });

    return { txId: data.txId };
  }

  /**
   * Like sendTransaction() — the dApp builds its own message BOC — but relayed through the WC flow
   * (POST /wc/sendTx) instead of /dex/sendTx: it binds the confirmed connect session and polls
   * /wc/status. The SDK fills appId, wallet_address, network and sessionToken; the device signs the
   * BOC and broadcasts.
   */
  async sendTxByMsgBoc(params: SendTransactionParams): Promise<SendTransactionResult> {
    await this.validateSession();

    const session = this.currentResult?.session;
    const address = this.currentResult?.wallet?.address;
    if (!session?.apiUrl || !address) {
      throw new Error('Wallet not connected');
    }

    const baseUrl = session.apiUrl.replace(/\/$/, '');
    let res: Response;
    try {
      res = await fetch(`${baseUrl}/wc/sendTx`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...params,
          appId: this.config.appId,
          ...(this.config.appName ? { appName: this.config.appName } : {}),
          ...(session.sessionToken ? { sessionToken: session.sessionToken } : {}),
          wallet_address: address,
          network: params.network || this.config.network || 'ton-mainnet',
        }),
      });
    } catch {
      throw new Error('Connection failed');
    }

    if (res.status === 401) throw new Error('Wallet not connected');
    if (res.status === 429) throw new Error('Too many pending transactions');
    if (!res.ok) throw new Error('Failed to submit transaction');

    const data = await res.json();
    this.storePending({ type: 'tx', txId: data.txId, apiUrl: session.apiUrl, statusPath: '/wc/status', ts: Date.now() });
    this.openWalletForTx();
    return { txId: data.txId };
  }

  /**
   * Easy payment: the dApp passes only the business params ({ type, amount, toAddress, action,
   * jettonMaster? }); the SDK fills appId, the connected wallet_address, and the network, and calls
   * POST /wc/pay. The backend builds the jetton/native transfer and the device signs it.
   */
  async pay(params: WcPayParams): Promise<SendTransactionResult> {
    await this.validateSession();

    const session = this.currentResult?.session;
    const address = this.currentResult?.wallet?.address;
    if (!session?.apiUrl || !address) {
      throw new Error('Wallet not connected');
    }

    const baseUrl = session.apiUrl.replace(/\/$/, '');
    let res: Response;
    try {
      res = await fetch(`${baseUrl}/wc/pay`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...params,
          appId: this.config.appId,
          ...(this.config.appName ? { appName: this.config.appName } : {}),
          // Binds this payment request to the confirmed connect session, so the backend can reject
          // sign requests that aren't from the dApp that actually connected (not just anyone who knows appId).
          ...(session.sessionToken ? { sessionToken: session.sessionToken } : {}),
          wallet_address: address,
          network: params.network || this.config.network || 'ton-mainnet',
        }),
      });
    } catch {
      throw new Error('Connection failed');
    }

    if (res.status === 401) throw new Error('Wallet not connected');
    if (res.status === 429) throw new Error('Too many pending transactions');
    if (!res.ok) throw new Error('Failed to submit payment');

    const data = await res.json();
    this.storePending({ type: 'tx', txId: data.txId, apiUrl: session.apiUrl, statusPath: '/wc/status', ts: Date.now() });
    this.openWalletForTx();
    return { txId: data.txId };
  }

  /**
   * Alias of pay(): same params, same backend call (POST /wc/pay) — the backend builds the
   * jetton/native transfer and the device signs it. Exposed under the `send` name for dApps that
   * prefer it; behaviour is identical to pay() (it delegates, so the two never diverge).
   */
  async send(params: WcPayParams): Promise<SendTransactionResult> {
    return this.pay(params);
  }

  /**
   * List the jettons payable with pay({ type: 'jetton' }) on a TON network — the whitelist that
   * avoids a "Jetton not supported" error. Works before or after connect.
   */
  async getJettons(
    network?: string,
  ): Promise<Array<{ master: string; symbol: string; decimals: number; name: string; logoURI?: string }>> {
    const base = (
      this.currentResult?.session?.apiUrl ||
      this.config.registryUrl.replace(/\/connect\/wallets\/?$/, '')
    ).replace(/\/$/, '');
    const query = network ? `?network=${encodeURIComponent(network)}` : '';
    let res: Response;
    try {
      res = await fetch(`${base}/wc/jettons${query}`);
    } catch {
      throw new Error('Connection failed');
    }
    if (!res.ok) throw new Error('Failed to fetch jettons');
    return res.json();
  }

  async waitForResult(
    txId: string,
    options?: WaitForResultOptions,
  ): Promise<TxStatusResult> {
    const session = this.currentResult?.session;
    if (!session?.apiUrl) {
      throw new Error('Wallet not connected');
    }

    const interval = options?.interval ?? 2000;
    const timeout = options?.timeout ?? 120000;
    const baseUrl = session.apiUrl.replace(/\/$/, '');
    // Route to the flow that created the tx: pay() → /wc/status, sendTransaction → /dex/status.
    const pending = this.readPending();
    const statusPath = options?.api
      ? `/${options.api}/status`
      : (pending?.txId === txId && pending?.statusPath) || '/dex/status';
    const isDex = statusPath === '/dex/status';
    const start = Date.now();

    while (true) {
      let res: Response;
      try {
        res = await fetch(`${baseUrl}${statusPath}/${txId}`);
      } catch {
        throw new Error('Connection failed');
      }

      if (!res.ok) {
        throw new Error('Failed to fetch transaction status');
      }

      const data = await res.json();
      const result: TxStatusResult = {
        status: data.status,
        msgHash: data.msgHash,
        action: data.action,
        reason: data.reason,
        // Payment details (present on the /wc/status path; undefined for /dex swaps).
        amount: data.amount,
        token: data.token,
        jettonMasterAddress: data.jettonMasterAddress,
        recipient: data.recipient,
        sender: data.sender,
        network: data.network,
        confirmedAt: data.confirmedAt,
      };

      if (data.status !== 'PENDING') {
        // Swap bị hủy -> báo BE để BE notify OneDex reject pending permit (fire-and-forget).
        // Chỉ áp cho flow dex (swap); flow /wc không có permit để reject.
        if (data.status === 'CANCELLED' && isDex) {
          fetch(`${baseUrl}/dex/push-tx`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ txId }),
          }).catch(() => {});
        }
        this.clearPending(); // tx đã có kết quả -> không cần resume nữa
        this.emit('resolved', result);
        return result;
      }

      this.emit('pending', result);

      if (Date.now() - start >= timeout) {
        throw new Error('Timeout');
      }

      await this.sleep(interval);
    }
  }

  isConnected(): boolean {
    return this.currentResult !== null;
  }

  getWallet(): ConnectResult | null {
    return this.currentResult;
  }

  on(event: WalletConnectEventType, cb: WalletConnectEventCallback): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(cb);
  }

  off(event: WalletConnectEventType, cb: WalletConnectEventCallback): void {
    this.listeners.get(event)?.delete(cb);
  }

  // ── Internal ──

  private get storageKey(): string {
    const walletId = this.config.walletId;
    const suffix = Array.isArray(walletId)
      ? walletId.sort().join('_')
      : walletId || 'all';
    return `${STORAGE_KEY_PREFIX}${suffix}__`;
  }

  private async fetchWallets(): Promise<WalletMeta[]> {
    const url = this.config.registryUrl.replace(/\/$/, '');
    const res = await fetch(url);
    if (!res.ok) throw new Error('Failed to fetch wallets');
    const wallets: WalletMeta[] = await res.json();
    // Branding rules (registry value wins when present):
    // - every wallet except tonpass shows "ONE COMPANY" in the Powered-by footer;
    // - tonpass uses its own blue accent, others fall back to the default green.
    return wallets.map((w) => ({
      ...w,
      company: w.walletId !== 'tonpass' ? 'ONE COMPANY' : w.company || w.name,
      accent: w.accent || this.config.accent || WALLET_ACCENTS[w.walletId],
      mono: w.mono ?? MONO_WALLETS.has(w.walletId),
    }));
  }

  private filterWallets(wallets: WalletMeta[]): WalletMeta[] {
    if (!this.config.walletId) return wallets;

    const ids = Array.isArray(this.config.walletId)
      ? this.config.walletId
      : [this.config.walletId];

    return wallets.filter((w) => ids.includes(w.walletId));
  }

  private async startConnect(
    wallet: WalletMeta,
    resolve: (result: ConnectResult) => void,
    reject: (error: Error) => void,
  ): Promise<void> {
    try {
      const session = await this.initSession(wallet);

      // Lưu pending TRƯỚC khi mở onewallet (onedex có thể bị đóng ngay sau đó).
      // Mở lại sẽ đọc cái này để biết phải check /connect/status.
      this.storePending({
        type: 'connect',
        sessionToken: session.sessionToken,
        walletId: wallet.walletId,
        walletName: wallet.name,
        walletAccent: wallet.accent,
        walletMono: wallet.mono,
        apiUrl: wallet.apiUrl,
        ts: Date.now(),
      });

      this.connectVia = 'qr'; // default; becomes 'deeplink' if the user clicks "Open in Telegram"
      // On web, keep the wallet open (QR scan or desktop window both use this deeplink).
      const deeplink = this.withConnectMarkers(session.deeplink);
      this.modal?.showConnect(wallet, deeplink, () => this.openWalletForConnect(deeplink));

      this.listenForConfirmation(
        wallet.apiUrl,
        session.sessionToken,
        async (result) => {
          // Attach session info for disconnect + reopening the wallet to sign later.
          result.session = {
            sessionToken: session.sessionToken,
            walletId: wallet.walletId,
            apiUrl: wallet.apiUrl,
            tma: { botUsername: wallet.botUsername, appName: wallet.appName },
            via: this.connectVia,
          };
          this.currentResult = result;
          // Persist BEFORE fetching tokens: the login token must never be written to localStorage.
          this.storeSession(result);
          this.clearPending(); // đã resolve ngay trong page -> không cần resume
          if (this.config.returnAT) {
            result.auth = await this.fetchAuth(wallet.apiUrl, session.sessionToken);
          }
          this.syncToWalletInstance(wallet.walletId, result);
          this.emit('connect', result);

          // Show success screen
          this.modal?.showSuccess(
            wallet,
            result.wallet?.address || null,
            result.user || null,
            () => {
              this.modal?.destroy();
              this.modal = null;
              resolve(result);
            },
          );
        },
        () => {
          this.clearPending();
          this.modal?.updateStatus('Connection rejected', false);
          setTimeout(() => {
            this.modal?.destroy();
            this.modal = null;
            reject(new Error('Connection rejected by user'));
          }, 1500);
        },
        () => {
          this.clearPending();
          this.modal?.updateStatus('Session expired', false);
          this.emit('session_expired');
          setTimeout(() => {
            this.modal?.destroy();
            this.modal = null;
            reject(new Error('Session expired'));
          }, 1500);
        },
      );
    } catch (err) {
      this.modal?.destroy();
      this.modal = null;
      reject(err instanceof Error ? err : new Error(String(err)));
    }
  }

  private async initSession(wallet: WalletMeta): Promise<InitSessionResponse> {
    const baseUrl = wallet.apiUrl.replace(/\/$/, '');
    const res = await fetch(`${baseUrl}/connect/init`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        appId: this.config.appId,
        walletId: wallet.walletId,
        origin: typeof window !== 'undefined' ? window.location.origin : '',
        siteTmaUrl: this.config.siteTmaUrl,
        // Platform nơi DApp đang chạy — tự lấy từ Telegram WebApp (rỗng nếu DApp mở ngoài Telegram).
        platform:
          (typeof window !== 'undefined' &&
            (window as any).Telegram?.WebApp?.platform) || '',
        ...(this.config.returnAT ? { returnAccessToken: true } : {}),
      }),
    });
    if (!res.ok) throw new Error('Failed to init session');
    return res.json();
  }

  // Fetch the login token the backend minted at confirm (returnAT flow). It is NOT pushed over the
  // public WS/status channel — only /connect/resolve returns it, once, then the backend clears it.
  private async fetchAuth(
    apiUrl: string,
    sessionToken: string,
  ): Promise<ConnectResult['auth']> {
    try {
      const baseUrl = apiUrl.replace(/\/$/, '');
      const res = await fetch(`${baseUrl}/connect/resolve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionToken, appId: this.config.appId }),
      });
      if (!res.ok) return undefined;
      const data = await res.json();
      return data?.auth;
    } catch {
      return undefined;
    }
  }

  private listenForConfirmation(
    apiUrl: string,
    sessionToken: string,
    onConfirmed: (result: ConnectResult) => void,
    onRejected: () => void,
    onExpired: () => void,
  ): void {
    let resolved = false;

    const wrap = <T extends (...args: any[]) => void>(fn: T): T =>
      ((...args: any[]) => {
        if (resolved) return;
        resolved = true;
        this.wsTransport?.close();
        this.pollingTransport?.stop();
        fn(...args);
      }) as unknown as T;

    const wrappedConfirm = wrap(onConfirmed);
    const wrappedReject = wrap(onRejected);
    const wrappedExpire = wrap(onExpired);

    // Start polling immediately as primary transport
    this.pollingTransport = new PollingTransport(
      apiUrl,
      sessionToken,
      wrappedConfirm,
      wrappedReject,
      wrappedExpire,
      (msg) => this.emit('error', msg),
    );
    this.pollingTransport.start();

    // Also try WS for faster response
    this.wsTransport = new WsTransport(
      apiUrl,
      sessionToken,
      wrappedConfirm,
      wrappedReject,
      wrappedExpire,
      () => {}, // WS fail is fine, polling is running
    );
    this.wsTransport.connect();
  }

  private cleanup(): void {
    this.wsTransport?.close();
    this.wsTransport = null;
    this.pollingTransport?.stop();
    this.pollingTransport = null;
    this.modal?.destroy();
    this.modal = null;
  }

  private emit(event: WalletConnectEventType, data?: any): void {
    this.listeners.get(event)?.forEach((cb) => cb(data));
  }

  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  // ── Persistence ──

  private storeSession(result: ConnectResult): void {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(result));
    } catch {
      // localStorage unavailable
    }
  }

  private restoreSession(): void {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        this.currentResult = JSON.parse(stored);
      }
    } catch {
      // ignore
    }
  }

  private removeStoredSession(): void {
    try {
      localStorage.removeItem(this.storageKey);
    } catch {
      // ignore
    }
  }

  /**
   * Auto-run lúc khởi tạo cho phiên ĐÃ connect (restore từ localStorage): hỏi server
   * /connect/status; nếu không còn CONFIRMED (connection hết hạn / bị disconnect phía ví)
   * thì tự xoá session của ĐÚNG ví này + emit 'disconnect'. KHÔNG dùng disconnect() để
   * tránh clearAllRelatedSessions cuốn phăng ví khác. Lỗi mạng -> giữ nguyên, thử lại lần sau.
   */
  private async validateSession(): Promise<void> {
    // Đang có luồng connect/tx resume dở (onewallet redirect về onedex, hoặc mobile giữ
    // onedex mở) -> để resumeConnect()/resume() xử lý phiên MỚI; KHÔNG validate session CŨ
    // rồi disconnect/reload đè lên flow connect đang chạy.
    if (this.readPending()) return;

    const session = this.currentResult?.session;
    if (!session?.apiUrl || !session.sessionToken) return;

    const baseUrl = session.apiUrl.replace(/\/$/, '');
    try {
      const res = await fetch(
        `${baseUrl}/connect/status?token=${session.sessionToken}`,
      );
      if (!res.ok) return; // lỗi tạm -> giữ session, thử lại lần mở sau
      const data = await res.json();

      if (data.status !== 'CONFIRMED') {
        // Hết hạn -> xoá thông tin trong localStorage + toast, KHÔNG reload. DApp đọc lại
        // (getWallet/isConnected trả null, hoặc bắt event 'disconnect') -> hiện nút Connect.
        this.currentResult = null;
        this.removeStoredSession(); // chỉ key ví này, KHÔNG clearAll
        this.emit('disconnect');
        Modal.toast('Session expired', this.config.theme);
      }
    } catch {
      // mạng lỗi -> giữ session để lần mở sau kiểm tra lại.
    }
  }

  // ── Pending (resume sau khi onedex đóng/mở lại) ──

  private get pendingKey(): string {
    return `${this.storageKey}pending`;
  }

  private storePending(data: Record<string, any>): void {
    try {
      localStorage.setItem(this.pendingKey, JSON.stringify(data));
    } catch {
      // ignore
    }
  }

  private readPending(): Record<string, any> | null {
    try {
      const raw = localStorage.getItem(this.pendingKey);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  private clearPending(): void {
    try {
      localStorage.removeItem(this.pendingKey);
    } catch {
      // ignore
    }
  }

  /**
   * Auto-run lúc khởi tạo — CHỈ xử lý CONNECT pending: check /connect/status,
   * tự bật modal (success/rejected/expired), set state + emit 'connect', rồi
   * XÓA pending khỏi storage. Không đụng tx (tx do DApp gọi resume()).
   */
  private async resumeConnect(): Promise<void> {
    const pending = this.readPending();
    if (!pending || pending.type !== 'connect') return;

    if (!pending.ts || Date.now() - pending.ts > PENDING_TTL_MS) {
      this.clearPending();
      return;
    }
    const baseUrl = String(pending.apiUrl || '').replace(/\/$/, '');
    if (!baseUrl) {
      this.clearPending();
      return;
    }

    try {
      const res = await fetch(
        `${baseUrl}/connect/status?token=${pending.sessionToken}`,
      );
      if (!res.ok) return; // lỗi tạm -> giữ pending, thử lại lần mở sau
      const data = await res.json();

      if (data.status === 'CONFIRMED') {
        const result: ConnectResult = {
          wallet: data.wallet ?? null,
          user: data.user ?? {},
          session: {
            sessionToken: pending.sessionToken,
            walletId: pending.walletId,
            apiUrl: pending.apiUrl,
          },
        };
        this.currentResult = result;
        this.storeSession(result); // persist BEFORE tokens — keep the login token out of localStorage
        if (this.config.returnAT) {
          result.auth = await this.fetchAuth(pending.apiUrl, pending.sessionToken);
        }
        this.syncToWalletInstance(pending.walletId, result);
        this.clearPending(); // xóa data resume sau khi đã xử lý
        this.showResumeConnectModal(
          'success',
          pending.walletName,
          result,
          pending.walletAccent,
          pending.walletMono,
        );
        this.emit('connect', result); // để DApp cập nhật state (button)
      } else if (data.status === 'REJECTED' || data.status === 'EXPIRED') {
        this.clearPending();
        this.showResumeConnectModal(
          data.status === 'REJECTED' ? 'rejected' : 'expired',
          pending.walletName,
          undefined,
          pending.walletAccent,
          pending.walletMono,
        );
      }
      // PENDING: chưa duyệt xong -> giữ pending.
    } catch {
      // mạng lỗi -> giữ pending để lần mở sau resume.
    }
  }

  /**
   * DApp gọi 1 lần lúc startup — CHỈ xử lý TX pending: check /dex/status, XÓA
   * pending, trả kết quả để DApp tự hiển thị (TxResultModal + số lượng).
   * Connect resume tự chạy ở constructor (resumeConnect).
   */
  async resume(): Promise<
    { type: 'tx'; status: string; result: TxStatusResult } | null
  > {
    const pending = this.readPending();
    if (!pending || pending.type !== 'tx') return null;

    if (!pending.ts || Date.now() - pending.ts > PENDING_TTL_MS) {
      this.clearPending();
      return null;
    }
    const baseUrl = String(pending.apiUrl || '').replace(/\/$/, '');
    if (!baseUrl) {
      this.clearPending();
      return null;
    }

    try {
      const statusPath = pending.statusPath || '/dex/status';
      const res = await fetch(`${baseUrl}${statusPath}/${pending.txId}`);
      if (!res.ok) return null;
      const data = await res.json();

      if (data.status !== 'PENDING') {
        this.clearPending(); // xóa data resume sau khi đã xử lý
        return {
          type: 'tx',
          status: data.status,
          result: {
            status: data.status,
            msgHash: data.msgHash,
            action: data.action,
            reason: data.reason,
          },
        };
      }
      // còn pending -> giữ.
    } catch {
      // mạng lỗi -> giữ pending.
    }
    return null;
  }

  /** Bật modal kết quả connect khi resume (đóng app -> mở lại), vì modal của
   *  connect() cũ đã bị hủy. Hỗ trợ cả success / rejected / expired. */
  private showResumeConnectModal(
    kind: 'success' | 'rejected' | 'expired',
    walletName?: string,
    result?: ConnectResult,
    accent?: string,
    mono?: boolean,
  ): void {
    try {
      const name = walletName || 'Wallet';
      const modal = new Modal(this.config.theme, () => modal.destroy());
      if (kind === 'success') {
        modal.showSuccess(
          { name, accent, mono } as WalletMeta,
          result?.wallet?.address ?? null,
          result?.user ?? null,
          () => modal.destroy(),
        );
      } else if (kind === 'rejected') {
        modal.showFailed(
          name,
          'Connection Rejected',
          'You rejected the connection in your wallet.',
          () => modal.destroy(),
          accent,
          mono,
        );
      } else {
        modal.showFailed(
          name,
          'Session Expired',
          'The connection request expired. Please try again.',
          () => modal.destroy(),
          accent,
          mono,
        );
      }
    } catch {
      // ignore (SSR / no DOM)
    }
  }

  // ── Global window registration ──

  private registerGlobal(): void {
    if (typeof window === 'undefined') return;

    const walletId = this.config.walletId;
    const key = Array.isArray(walletId)
      ? walletId[0]
      : walletId || 'walletconnect';

    (window as any)[key] = this;
  }

  /** Sync connect result to the specific wallet instance on window (e.g. window.onedollar) */
  private syncToWalletInstance(walletId: string, result: ConnectResult): void {
    if (typeof window === 'undefined') return;

    const target = (window as any)[walletId];
    if (target && target !== this && target instanceof BaseWalletConnect) {
      (target as any).currentResult = result;
      (target as any).storeSession(result);
    }
  }

  /** Clear all related sessions from all window instances and localStorage */
  private clearAllRelatedSessions(): void {
    if (typeof window === 'undefined') return;

    // Find all BaseWalletConnect instances on window and clear them
    try {
      for (const key of Object.keys(window)) {
        const inst = (window as any)[key];
        if (inst && inst !== this && inst instanceof BaseWalletConnect) {
          (inst as any).currentResult = null;
          (inst as any).removeStoredSession();
        }
      }
    } catch {
      // ignore
    }

    // Clear all wc localStorage keys
    try {
      const toRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith(STORAGE_KEY_PREFIX)) {
          toRemove.push(k);
        }
      }
      toRemove.forEach((k) => localStorage.removeItem(k));
    } catch {
      // ignore
    }
  }
}
